// lib
import React, { useContext, useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import { generateUID, plural } from '@aytm/app/stats/lib/helpers'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import Attribute from '@aytm/app/stats/page/question-views/components/mapping/Attribute'

// styles
import style from '@aytm/app/stats/page/question-views/components/mapping/styles/Component.module.scss'

const onDNDPositionChange = ({ newPosition, dragItemUpdater } = {}) =>
  dragItemUpdater(node => {
    const itemPositionNode = node.querySelector(`.stats-Concept-Item-index`)
    if (null !== itemPositionNode) {
      itemPositionNode.innerHTML = null === newPosition ? '' : `${newPosition + 1}.`
    }
  })

const getDefaultAttribute = (item = {}) => {
  return {
    key: generateUID(),
    text: '',
    refs: '',
    method: '',
    ...item
  }
}

const Attributes = ({ items, dimension, onModeChange }) => {
  const { id, mode, group, data, disabled, dispatch } = useContext(DataContext)

  const [activeLabel, setActiveLabel] = useState('&nbsp;')

  const dndItemsListRef = useRef()

  const methodTimer = useRef(null)

  const onDNDUpdateItemPositions = ({ hiddenItemIndex, placeholderIndex } = {}) => {
    const itemPositionNodes = dndItemsListRef.current.querySelectorAll(`.stats-Concept-Item .stats-Concept-Item-index`)

    for (const [i, itemPositionNode] of itemPositionNodes.entries()) {
      if (null !== itemPositionNode) {
        const visualItemIndex = i + (null !== hiddenItemIndex && hiddenItemIndex <= i ? -1 : 0)
        const newItemIndex =
          visualItemIndex + (null !== placeholderIndex && placeholderIndex <= visualItemIndex ? 1 : 0)
        itemPositionNode.innerHTML = null === newItemIndex ? '' : `${newItemIndex + 1}.`
      }
    }
  }

  const onDNDDrop = res => {
    const { item, position, clone } = res

    if (clone) {
      dispatch({
        type: 'ADD_ATTRIBUTE',
        data: {
          item: {
            ...getDefaultAttribute(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.id != id) {
      item.remove()
      dispatch({
        type: 'ADD_ATTRIBUTE',
        data: {
          item: {
            ...getDefaultAttribute(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.position != position) {
      dispatch({
        type: 'REORDER_ATTRIBUTES',
        data: {
          from: item.position,
          to: position
        }
      })
    }

    onDNDUpdateItemPositions()
  }

  const dndItemsList = ADND.targets
    .itemsList(
      {
        acceptTypes: [`dlq-attribute-${data.windowUID}`],
        placeholderVMargin: 10
      },
      { capture: false }
    )
    .buildPlaceholder(() => {
      const placeholderNode = document.createElement('div')
      placeholderNode.className = `${style.placeholder}`
      return placeholderNode
    })
    .onPositionChange(onDNDPositionChange)
    .onUpdateItemPositions(onDNDUpdateItemPositions)
    .onEnter(onDNDUpdateItemPositions)
    .onLeave(onDNDUpdateItemPositions)
    .onDrop(onDNDDrop)

  const onAddClick = () => {
    dispatch({
      type: 'ADD_ATTRIBUTE',
      data: {
        item: getDefaultAttribute(),
        position: items.length
      }
    })
  }

  const onMethodBlur = () => {
    if (null !== methodTimer.current) return
    setActiveLabel('&nbsp;')
  }

  const onMethodFocus = useCallback(() => {
    methodTimer.current && clearTimeout(methodTimer.current)
    methodTimer.current = setTimeout(() => {
      setActiveLabel('Hold <u>Shift</u> to apply to all')
    }, 200)

    return () => methodTimer.current && clearTimeout(methodTimer.current)
  }, [])

  useEffect(() => {
    dndItemsList.connect(dndItemsListRef.current)

    return () => {
      dndItemsList.disconnect()
    }
  }, [dndItemsList])

  return (
    <div className={style.component}>
      <div className={`${style.row} ${style['row-header']} p-fluid p-col-12`}>
        {disabled || group == undefined || null === group || ['custom', 'survey'].includes(group) ? (
          <b className={style.title}>
            {items.length} {plural(items.length, 'attribute', 'attributes')}
          </b>
        ) : (
          <>
            <div className="p-field p-col-4">
              <b className={style.title}>
                {items.length} {plural(items.length, 'attribute', 'attributes')}
              </b>
            </div>
            <div className="p-field p-right p-col-8">
              <span className={style.toggle} onClick={() => onModeChange('advanced' == mode ? 'basic' : 'advanced')}>
                {'advanced' == mode ? 'switch to basic mode' : 'switch to advanced mode'}
              </span>
            </div>
          </>
        )}
      </div>
      <div
        className={`${style.row} ${style['row-label']}${
          disabled ? ' ' + style['row-label-disabled'] : ''
        } p-fluid p-col-12`}
      >
        <div className="p-field p-col-6">
          Questions/Subquestions
          <Popup
            trigger="click"
            width="400px"
            direction="righttop"
            popupOffset={14}
            hideOtherPopupsOnShow
            content={() => (
              <>
                <p>Select the following for questions/subquestions:</p>
                <ol className="rfd-ui-popup-list">
                  <li>The question(s) or subquestion(s) you would like to include as attributes.</li>
                  <li>
                    How you would like the question/subquestion summarized (mean, box scores, or an individual answer
                    option).
                  </li>
                  <li>
                    Review definitions for mean and box score options&nbsp;
                    <a
                      href="https://helpcenter.aytm.com/hc/en-us/articles/11652523287053-Concept-Lab"
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </li>
                </ol>
              </>
            )}
          >
            <i className="icon-aytmfd-help" />
          </Popup>
        </div>
        <div className={`${style.group} p-fluid p-field p-col`}>
          <div className="p-field p-col-5" dangerouslySetInnerHTML={{ __html: activeLabel }}></div>
          <div className="p-field p-col">Label</div>
        </div>
      </div>
      <div ref={dndItemsListRef} className={style.attributes}>
        {items.map((item, index) => (
          <Attribute
            key={item.key}
            item={item}
            position={index + 1}
            dimension={dimension}
            dndItemsList={dndItemsList}
            onMethodFocus={onMethodFocus}
            onMethodBlur={onMethodBlur}
          />
        ))}
      </div>
      {disabled ? null : (
        <div className={`${style.row} ${style['row-add']} p-fluid p-col-12`}>
          <div className={`${style.index} p-field`}>{items.length + 1}.</div>
          <div className="p-field">
            <span className={style.add} onClick={onAddClick}>
              Add an attribute
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

Attributes.propTypes = {
  items: PropTypes.array,
  dimension: PropTypes.number,
  onModeChange: PropTypes.func
}

Attributes.displayName = 'Attributes'

export default Attributes

ADND().connect()
